import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  IoDocumentTextOutline,
  IoCodeSlashOutline,
  IoTimeOutline,
  IoCloseCircleOutline,
  IoCheckmarkCircle,
  IoOpenOutline,
  IoReload,
  IoReceiptOutline,
} from 'react-icons/io5';
//
import apiFilas from '../../services/apiFilas';
import LoadingCircle from '../../components/LoadingCircle';
import Botao from '../../components/Botao';
//
import styles from './nota.module.css';

interface NotaData {
  id?: number;
  paymentasaasid?: string;
  parceiro?: string | null;
  tipo?: string | null;
  nfcom_id?: string | null;
  nfe_key?: string | null;
  series?: string | null;
  status?: string | null;
  environment?: string | null;
  qrcode_url?: string | null;
  pdf_url?: string | null;
  xml_url?: string | null;
  submitted_at?: string | null;
  authorized_at?: string | null;
  error_message?: string | null;
}

type ApiResponse =
  | { success: true; data: NotaData }
  | { success: false; message?: string };

type State =
  | { kind: 'loading' }
  | { kind: 'sem_payid' }
  | { kind: 'sem_fatura' }       // 404 do apifilas → registro ainda nao criado
  | { kind: 'aguardando_emissao' } // registro existe mas nfcom_id NULL
  | { kind: 'processando' }       // DRAFT/SUBMITTING
  | { kind: 'autorizada'; nota: NotaData }
  | { kind: 'erro_emissao'; nota: NotaData }
  | { kind: 'cancelada'; nota: NotaData }
  | { kind: 'falha_rede' };

const STATUS_FINAL = ['SUBMITTED', 'AUTHORIZED', 'VALIDATED'];
const STATUS_PROCESSANDO = ['DRAFT', 'PENDING_SUBMISSION', 'SUBMITTING'];
const STATUS_ERRO = ['SUBMISSION_ERROR', 'REJECTED', 'CANCELLATION_ERROR'];
const STATUS_CANCELADO = ['CANCELLED', 'PENDING_CANCELLATION', 'CANCELLING'];

function classify(data: NotaData): State {
  if (!data.nfcom_id) return { kind: 'aguardando_emissao' };

  const st = (data.status || '').toUpperCase();

  if (STATUS_FINAL.includes(st) && data.nfe_key) {
    return { kind: 'autorizada', nota: data };
  }
  if (STATUS_PROCESSANDO.includes(st) || (STATUS_FINAL.includes(st) && !data.nfe_key)) {
    return { kind: 'processando' };
  }
  if (STATUS_CANCELADO.includes(st)) {
    return { kind: 'cancelada', nota: data };
  }
  if (STATUS_ERRO.includes(st)) {
    return { kind: 'erro_emissao', nota: data };
  }
  return { kind: 'processando' };
}

function formatDateBR(iso?: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatChave(k?: string | null): string {
  if (!k) return '';
  return k.replace(/(\d{4})/g, '$1 ').trim();
}

export default function Nota() {
  const [searchParams] = useSearchParams();
  const payid = searchParams.get('payid');

  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!payid) {
      setState({ kind: 'sem_payid' });
      return;
    }

    const cancelToken = axios.CancelToken.source();

    apiFilas
      .get<ApiResponse>(`/nfcom/full/${encodeURIComponent(payid)}`, {
        cancelToken: cancelToken.token,
      })
      .then((response) => {
        const body = response.data;
        if (!body || body.success === false || !('data' in body) || !body.data) {
          setState({ kind: 'sem_fatura' });
          return;
        }
        setState(classify(body.data));
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        const status = error?.response?.status;
        if (status === 404) {
          setState({ kind: 'sem_fatura' });
        } else if (!error?.response) {
          setState({ kind: 'falha_rede' });
        } else {
          setState({ kind: 'sem_fatura' });
        }
      });

    return () => cancelToken.cancel();
  }, [payid]);

  if (state.kind === 'loading') {
    return (
      <div className={styles['loading']}>
        <LoadingCircle />
      </div>
    );
  }

  if (state.kind === 'autorizada') {
    return renderAutorizada(state.nota);
  }

  return renderMensagem(state);
}

function renderAutorizada(nota: NotaData) {
  const chave = nota.nfe_key || '';
  const qrUrl = nota.qrcode_url || '';
  const pay = nota.paymentasaasid || '';

  // O apifilas faz proxy do PDF/XML direto da TelcomNFe quando ha nfcom_id valido,
  // entao os links de download sao deterministicos pela paymentasaasid.
  const base = apiFilas.defaults.baseURL?.replace(/\/$/, '') || '';
  const pdfUrl = pay ? `${base}/nfcom/full/${encodeURIComponent(pay)}/pdf` : '';
  const xmlUrl = pay ? `${base}/nfcom/full/${encodeURIComponent(pay)}/xml` : '';

  const isHomolog = (nota.environment || '').toLowerCase() === 'homologacao';

  return (
    <div className={styles['container']}>
      <div className={styles['card']}>
        <div
          className={styles['icon-wrapper']}
          style={{
            background: 'var(--color-success-bg)',
            borderColor: 'var(--color-success-border)',
            color: 'var(--color-success)',
          }}
        >
          <IoCheckmarkCircle />
        </div>

        <h1 className={styles['title']}>Nota Fiscal de Comunicação</h1>
        <p className={styles['subtitle']}>Autorizada pela SEFAZ</p>

        {isHomolog && (
          <span className={styles['badge-homolog']}>
            Ambiente de homologação (sem efeito fiscal)
          </span>
        )}

        <div className={styles['info-grid']}>
          <div className={styles['info-row']}>
            <span className={styles['info-label']}>Série / Número</span>
            <span className={styles['info-value']}>
              {nota.series || '—'}
              {chave ? ` / ${chave.substring(25, 34).replace(/^0+/, '') || '—'}` : ''}
            </span>
          </div>

          <div className={styles['info-row']}>
            <span className={styles['info-label']}>Autorizada em</span>
            <span className={styles['info-value']}>
              {formatDateBR(nota.authorized_at || nota.submitted_at)}
            </span>
          </div>

          {chave && (
            <div className={styles['info-row-full']}>
              <span className={styles['info-label']}>Chave de Acesso</span>
              <span className={styles['info-chave']}>{formatChave(chave)}</span>
            </div>
          )}
        </div>

        <div className={styles['actions']}>
          {pdfUrl && (
            <a className={styles['action-primary']} href={pdfUrl} target="_blank" rel="noopener noreferrer">
              <IoDocumentTextOutline />
              <span>Baixar PDF</span>
              <IoOpenOutline className={styles['action-icon-end']} />
            </a>
          )}

          {xmlUrl && (
            <a className={styles['action-secondary']} href={xmlUrl} target="_blank" rel="noopener noreferrer">
              <IoCodeSlashOutline />
              <span>Baixar XML</span>
              <IoOpenOutline className={styles['action-icon-end']} />
            </a>
          )}

          {qrUrl && (
            <a className={styles['action-secondary']} href={qrUrl} target="_blank" rel="noopener noreferrer">
              <IoReceiptOutline />
              <span>Consultar na SEFAZ</span>
              <IoOpenOutline className={styles['action-icon-end']} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function renderMensagem(state: Exclude<State, { kind: 'loading' } | { kind: 'autorizada' }>) {
  const copy = COPY[state.kind];
  return (
    <div className={styles['container']}>
      <div className={styles['card']}>
        <div
          className={styles['icon-wrapper']}
          style={{ background: copy.iconBg, borderColor: copy.iconBorder, color: copy.iconColor }}
        >
          {copy.icon}
        </div>
        <h1 className={styles['title']}>{copy.title}</h1>
        <p className={styles['message']}>{copy.message}</p>
        {copy.showRetry && (
          <Botao
            icon={<IoReload />}
            text="Tentar novamente"
            onClick={() => window.location.reload()}
            color="primary"
            style={{ marginTop: '1rem' }}
          />
        )}
      </div>
    </div>
  );
}

interface Copy {
  title: string;
  message: string;
  icon: React.ReactNode;
  iconBg: string;
  iconBorder: string;
  iconColor: string;
  showRetry: boolean;
}

const COPY: Record<
  Exclude<State['kind'], 'loading' | 'autorizada'>,
  Copy
> = {
  sem_payid: {
    title: 'Cobrança não informada',
    message: 'Use o link enviado pela operadora para acessar sua nota fiscal.',
    icon: <IoReceiptOutline />,
    iconBg: 'var(--color-info-bg)',
    iconBorder: 'var(--color-info-border)',
    iconColor: 'var(--color-info)',
    showRetry: false,
  },
  sem_fatura: {
    title: 'Nota fiscal indisponível',
    message:
      'Sua nota fiscal ainda não está disponível. Isso geralmente acontece quando a cobrança ainda não foi paga ou está aguardando confirmação. Tente novamente em alguns minutos.',
    icon: <IoTimeOutline />,
    iconBg: 'var(--color-info-bg)',
    iconBorder: 'var(--color-info-border)',
    iconColor: 'var(--color-info)',
    showRetry: true,
  },
  aguardando_emissao: {
    title: 'Nota fiscal em preparação',
    message:
      'Recebemos seu pagamento e estamos preparando a emissão da nota fiscal. O processo é automático e leva alguns minutos.',
    icon: <IoTimeOutline />,
    iconBg: 'var(--color-warning-bg)',
    iconBorder: 'var(--color-warning-border)',
    iconColor: 'var(--color-warning)',
    showRetry: true,
  },
  processando: {
    title: 'Aguardando autorização',
    message:
      'Sua nota fiscal foi enviada e está aguardando autorização pela SEFAZ. Isso costuma levar de 1 a 5 minutos.',
    icon: <IoTimeOutline />,
    iconBg: 'var(--color-warning-bg)',
    iconBorder: 'var(--color-warning-border)',
    iconColor: 'var(--color-warning)',
    showRetry: true,
  },
  erro_emissao: {
    title: 'Não foi possível emitir a nota',
    message:
      'Tivemos um problema ao emitir sua nota fiscal junto à SEFAZ. Por favor entre em contato com a operadora para regularizar.',
    icon: <IoCloseCircleOutline />,
    iconBg: 'var(--color-error-bg)',
    iconBorder: 'var(--color-error-border)',
    iconColor: 'var(--color-error)',
    showRetry: false,
  },
  cancelada: {
    title: 'Nota fiscal cancelada',
    message:
      'Esta nota fiscal foi cancelada. Caso tenha dúvidas, entre em contato com a operadora.',
    icon: <IoCloseCircleOutline />,
    iconBg: 'var(--color-info-bg)',
    iconBorder: 'var(--color-info-border)',
    iconColor: 'var(--color-info)',
    showRetry: false,
  },
  falha_rede: {
    title: 'Falha de conexão',
    message:
      'Não conseguimos verificar sua nota fiscal agora. Confira sua conexão com a internet e tente novamente.',
    icon: <IoCloseCircleOutline />,
    iconBg: 'var(--color-error-bg)',
    iconBorder: 'var(--color-error-border)',
    iconColor: 'var(--color-error)',
    showRetry: true,
  },
};
